import { Plus, X } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { applyFieldErrors, ErrorState, useErrorMessage } from '@nucleus-commerce/admin-kit/errors';
import { Badge } from '@nucleus-commerce/ui/components/badge';
import { Button } from '@nucleus-commerce/ui/components/button';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@nucleus-commerce/ui/components/form';
import { Input } from '@nucleus-commerce/ui/components/input';
import { Skeleton } from '@nucleus-commerce/ui/components/skeleton';
import { toast } from '@nucleus-commerce/ui/components/sonner';

import { useI18nResolver } from '@/shared/forms/use-i18n-resolver';
import { SECTION_ANCHORS } from '@/shared/navigation/section-anchors';

import { useApplePayDomains } from '../hooks/use-apple-pay-domains';
import { useAddApplePayDomain, useRemoveApplePayDomain } from '../hooks/use-stripe-mutations';
import { isProviderRejected } from '../schemas/stripe-rules';
import { applePayDomainSchema, type ApplePayDomainValues } from '../schemas/stripe-schemas';

const KNOWN_FIELDS: ReadonlySet<string> = new Set(['domain']);

export interface ApplePayDomainsProps {
  storeId: string;
  /** Adding and removing need settings.write and an open store. */
  editable: boolean;
  /** Proposed when the store has one and it is not registered yet. */
  suggestedDomain?: string | null;
}

/**
 * Apple Pay works only on domains registered with the connected account, and the storefront
 * must already serve Stripe's association file there; Stripe checks it when the domain is added.
 */
export function ApplePayDomains({ storeId, editable, suggestedDomain }: ApplePayDomainsProps) {
  const { t } = useTranslation(['paymentMethods', 'common']);
  const query = useApplePayDomains(storeId);
  const add = useAddApplePayDomain(storeId);
  const remove = useRemoveApplePayDomain(storeId);
  const message = useErrorMessage();
  const domains = query.data ?? [];
  const suggestion =
    suggestedDomain && query.data && !query.data.includes(suggestedDomain) ? suggestedDomain : '';
  const form = useForm<ApplePayDomainValues>({
    resolver: useI18nResolver(applePayDomainSchema),
    values: { domain: suggestion },
    resetOptions: { keepDirtyValues: true },
  });

  const submit = form.handleSubmit(async (values) => {
    try {
      await add.mutateAsync(values);
      form.reset({ domain: '' });
    } catch (error) {
      if (applyFieldErrors(error, form.setError, { knownFields: KNOWN_FIELDS })) return;
      form.setError('domain', {
        type: 'server',
        message: isProviderRejected(error)
          ? t('paymentMethods:stripe.applePay.rejected')
          : message(error),
      });
    }
  });

  return (
    <section
      id={SECTION_ANCHORS.applePay}
      aria-labelledby="apple-pay-domains-title"
      data-tour="settings.payment-methods.apple-pay"
      className="flex flex-col gap-3 border-t pt-4"
    >
      <div>
        <h3 id="apple-pay-domains-title" className="text-sm font-medium">
          {t('paymentMethods:stripe.applePay.title')}
        </h3>
        <p className="text-muted-foreground text-sm">
          {t('paymentMethods:stripe.applePay.description')}
        </p>
      </div>
      {query.isPending ? (
        <Skeleton className="h-6 w-48" />
      ) : query.isError ? (
        <ErrorState error={query.error} onRetry={() => void query.refetch()} compact />
      ) : domains.length === 0 ? (
        <p className="text-muted-foreground text-sm">{t('paymentMethods:stripe.applePay.none')}</p>
      ) : (
        <ul
          className="flex flex-wrap gap-1.5"
          aria-label={t('paymentMethods:stripe.applePay.list')}
        >
          {domains.map((domain) => (
            <li key={domain}>
              <Badge variant="secondary" className="gap-1 font-mono text-[12px]">
                {domain}
                {editable ? (
                  <button
                    type="button"
                    className="hover:bg-foreground/10 rounded-full p-0.5"
                    aria-label={t('paymentMethods:stripe.applePay.remove', { domain })}
                    disabled={remove.isPending}
                    onClick={() =>
                      remove.mutate(domain, { onError: (error) => toast.error(message(error)) })
                    }
                  >
                    <X className="size-3" aria-hidden />
                  </button>
                ) : null}
              </Badge>
            </li>
          ))}
        </ul>
      )}
      {editable ? (
        <Form {...form}>
          <form
            onSubmit={submit}
            noValidate
            className="flex flex-col gap-2 sm:flex-row sm:items-start"
            aria-label={t('paymentMethods:stripe.applePay.formLabel')}
          >
            <FormField
              control={form.control}
              name="domain"
              render={({ field }) => (
                <FormItem className="flex-1">
                  <FormLabel>{t('paymentMethods:stripe.applePay.domain')}</FormLabel>
                  <FormControl>
                    <Input
                      autoComplete="off"
                      autoCapitalize="off"
                      spellCheck={false}
                      placeholder="shop.example.com"
                      className="font-mono text-[13px]"
                      {...field}
                    />
                  </FormControl>
                  <FormDescription>
                    {t('paymentMethods:stripe.applePay.domainHint')}
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
            <Button type="submit" variant="outline" className="sm:mt-6" disabled={add.isPending}>
              <Plus aria-hidden />
              {add.isPending
                ? t('paymentMethods:stripe.applePay.adding')
                : t('paymentMethods:stripe.applePay.add')}
            </Button>
          </form>
        </Form>
      ) : null}
    </section>
  );
}
